
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { getCourierByPhone } from '@/utils/supabase/courier';

export interface CourierInfo {
  id: string | null;
  name: string;
  bikeId: string | null;
}

export function useCourierInfo() {
  const { user, isAdmin } = useAuth();
  const [courierId, setCourierId] = useState<string | null>(null);
  const [courierName, setCourierName] = useState<string>('');
  const [bikeId, setBikeId] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourierInfo = async () => {
      console.log('Начинаем загрузку информации о курьере');
      
      if (!user) {
        console.log('Пользователь не авторизован, нельзя загрузить данные курьера');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Получаем телефон пользователя
        const userPhone = user.phone;
        console.log('Телефон пользователя:', userPhone);
        
        // Сохраняем номер телефона из user context
        setPhone(userPhone);
        
        // Проверка на админа - если пользователь админ, он может войти без проверки в таблице курьеров
        if (isAdmin) {
          console.log('Пользователь является администратором, пропускаем проверку в таблице курьеров');
          // Для администратора устанавливаем специальный ID и имя из профиля
          setCourierId(user.id); // Используем ID из профиля
          setCourierName(user.name || 'Администратор'); // Используем имя из профиля
          setLoading(false);
          return;
        }
        
        // Для не-администраторов - стандартная логика проверки
        if (userPhone) {
          console.log('Поиск курьера по телефону:', userPhone);
          const { data: courierData, error: courierError } = await getCourierByPhone(userPhone);

          if (courierData) {
            console.log('Найден курьер по телефону:', courierData);
            console.log('ID КУРЬЕРА ИЗ ТАБЛИЦЫ COURIERS:', courierData.id);
            
            // Используем ID именно из таблицы couriers
            setCourierId(courierData.id);
            
            // Используем имя именно из таблицы couriers
            setCourierName(courierData.name || 'Неизвестный курьер');
            
            // Находим ID велосипеда, закрепленного за курьером по ID из таблицы couriers
            const { data: bikeData, error: bikeError } = await supabase
              .from('bikes')
              .select('id')
              .eq('assigned_to', courierData.id)
              .limit(1);

            if (bikeError) {
              console.error('Ошибка при загрузке привязанного велосипеда:', bikeError);
            } else if (bikeData && bikeData.length > 0) {
              console.log('Найден велосипед, закрепленный за курьером:', bikeData[0].id);
              setBikeId(bikeData[0].id);
            } else {
              console.log('Нет велосипеда, привязанного к курьеру');
            }
            
          } else {
            console.error('Курьер не найден по номеру телефона:', courierError || 'Неизвестная ошибка');
            setError('Учетная запись курьера не найдена. Обратитесь к менеджеру.');
            toast.error('Учетная запись курьера не найдена. Обратитесь к менеджеру.');
          }
        } else {
          console.log('У пользователя не указан номер телефона');
          setError('Для работы с приложением необходим номер телефона');
          toast.error('Для работы с приложением необходим номер телефона');
        }
        
        // Для тестового пользователя используем известный ID из таблицы couriers
        if (userPhone === '+71234567890' || user.email === 'courier@example.com') {
          // ID тестового курьера должен быть фиксированным
          const testCourierId = '99b0de4a-1700-4404-8bc8-2addb287df5d';
          const testCourierName = 'Тестовый Курьер';

          console.log('Устанавливаем тестового курьера с ID:', testCourierId);
          console.log('ID ТЕСТОВОГО КУРЬЕРА ИЗ ТАБЛИЦЫ COURIERS:', testCourierId);
          
          setCourierId(testCourierId);
          setCourierName(testCourierName);
          
          // Находим ID велосипеда, закрепленного за тестовым курьером
          const { data: bikeData } = await supabase
            .from('bikes')
            .select('id')
            .eq('assigned_to', testCourierId)
            .limit(1);

          if (bikeData && bikeData.length > 0) {
            console.log('Найден велосипед, закрепленный за курьером:', bikeData[0].id);
            setBikeId(bikeData[0].id);
          } else {
            console.log('Нет велосипеда, привязанного к тестовому курьеру');
          }
        }
      } catch (error: any) {
        console.error('Ошибка при загрузке данных курьера:', error);
        toast.error('Не удалось загрузить информацию о курьере');
        setError(error.message || 'Неизвестная ошибка');
      } finally {
        setLoading(false);
      }
    };

    fetchCourierInfo();
  }, [user, isAdmin]);

  return {
    courierId,
    courierName,
    bikeId,
    phone,
    loading,
    error
  };
}
